import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Camera, Check, X, Loader2, Refrigerator, Snowflake, Archive } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { MillieAvatar } from "./MascotBubble";

const LOCATIONS = [{ key: "fridge", i: Refrigerator, l: "Fridge" }, { key: "freezer", i: Snowflake, l: "Freezer" }, { key: "pantry", i: Archive, l: "Pantry" }];

async function compressImage(file, max = 1280) {
  const url = URL.createObjectURL(file);
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(url);
  return canvas.toDataURL("image/jpeg", 0.8).split(",")[1];
}

// onAdded(inventory) when a household exists; onItems(names) in form mode (no household yet). Accepts several photos at once.
export default function KitchenScan({ activeId, onAdded, onItems }) {
  const fileRef = useRef(null);
  const [location, setLocation] = useState("fridge");
  const [scanning, setScanning] = useState(false);
  const [found, setFound] = useState(null);
  const [picked, setPicked] = useState({});
  const [saving, setSaving] = useState(false);

  const pick = (loc) => { setLocation(loc); fileRef.current?.click(); };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setScanning(true);
    const merged = new Map();
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      toast.loading(files.length > 1 ? `Millie's having a squiz at photo ${i + 1} of ${files.length}…` : "Millie's having a squiz at your photo…", { id: "scan" });
      try {
        const image_base64 = await compressImage(files[i]);
        const { data } = await api.post("/kitchen/scan", { household_id: activeId || null, image_base64, location });
        data.items.forEach((it) => { const k = it.name.toLowerCase(); const prev = merged.get(k); if (!prev || it.confidence > prev.confidence) merged.set(k, it); });
      } catch (err) { failed++; toast.error(formatApiErrorDetail(err.response?.data?.detail), { id: "scan" }); }
    }
    setScanning(false);
    const items = [...merged.values()];
    if (!items.length) { if (!failed) toast.error("Couldn't spot any food in those — try again with more light", { id: "scan" }); return; }
    setFound(items);
    setPicked(Object.fromEntries(items.map((it) => [it.name, it.confidence >= 0.5])));
    toast.success(`Spotted ${items.length} things across ${files.length - failed} photo${files.length - failed === 1 ? "" : "s"}!`, { id: "scan" });
  };

  const confirm = async () => {
    const items = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
    if (!items.length) return setFound(null);
    if (onItems) { onItems(items); toast.success(`Added ${items.length} items — I'll plan around them 🙌`); setFound(null); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/kitchen/confirm", { household_id: activeId, items });
      onAdded(data.inventory);
      toast.success(`Added ${items.length} items to your kitchen 🙌`);
      setFound(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFile} data-testid="kitchen-scan-file" />
      <div className="grid grid-cols-3 gap-3">
        {LOCATIONS.map(({ key, i: Ic, l }) => (
          <button key={key} onClick={() => pick(key)} disabled={scanning} data-testid={`kitchen-scan-${l}`}
            className="bg-white rounded-3xl border-2 border-[#FDECEF] shadow-soft py-4 flex flex-col items-center gap-1 text-sorted-ink disabled:opacity-60">
            <span className="w-10 h-10 rounded-2xl bg-[#FBE6DC] flex items-center justify-center text-orange-500">
              {scanning && location === key ? <Loader2 size={20} className="animate-spin" /> : <Ic size={20} strokeWidth={2.5} />}
            </span>
            <span className="text-xs font-bold">{l}</span>
            <span className="flex items-center gap-0.5 text-[10px] font-bold text-sorted-slate"><Camera size={10} strokeWidth={2.5} />Snap 1+</span>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {found && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-sorted-ink/40 backdrop-blur-sm flex items-end justify-center" data-testid="scan-confirm-modal">
            <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} className="w-full max-w-md bg-[#FFF6F1] rounded-t-[2rem] p-6 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center gap-3">
                <MillieAvatar size={44} float={false} mood="surprised" />
                <div className="flex-1">
                  <p className="font-hand text-2xl text-sorted-ink leading-none">Here's what I spotted</p>
                  <p className="text-xs text-sorted-slate font-semibold">Untick anything I got wrong, then add.</p>
                </div>
                <button onClick={() => setFound(null)} aria-label="Close" data-testid="scan-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-sorted-slate"><X size={16} strokeWidth={3} /></button>
              </div>
              <div className="mt-4 space-y-2">
                {found.map((it) => (
                  <button key={it.name} onClick={() => setPicked({ ...picked, [it.name]: !picked[it.name] })} data-testid={`scan-item-${it.name}`}
                    className={`w-full flex items-center gap-3 bg-white rounded-2xl px-4 py-3 border-2 text-left transition-colors ${picked[it.name] ? "border-sorted-peach" : "border-[#FDECEF] opacity-70"}`}>
                    <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${picked[it.name] ? "bg-sorted-peach border-sorted-peach" : "border-sorted-blush"}`}>
                      {picked[it.name] && <Check size={14} strokeWidth={3.5} className="text-sorted-ink" />}
                    </span>
                    <span className="flex-1 font-semibold text-sorted-ink capitalize">{it.name}</span>
                    {it.quantity && <span className="text-xs font-bold text-sorted-slate">{it.quantity}</span>}
                    {it.confidence < 0.6 && <span className="text-[10px] font-bold bg-[#FAD9CE] text-sorted-ink px-2 py-0.5 rounded-full">not sure</span>}
                  </button>
                ))}
              </div>
              <button onClick={confirm} disabled={saving} data-testid="scan-confirm" className="mt-5 w-full bg-sorted-rose text-white font-bold py-4 rounded-full flex items-center justify-center gap-2">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} strokeWidth={2.5} />} Add {Object.values(picked).filter(Boolean).length} to my kitchen
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
